<template>
    <div class="pa-4">
        <!-- Lista -->
        <template v-if="!showForm">
            <div v-if="loading" class="text-center py-6">
                <v-progress-circular indeterminate :color="primaryColor" />
            </div>

            <div v-else-if="!enderecos.length" class="text-center py-6 grey--text">
                <v-icon size="42" class="mb-2">mdi-map-marker-off-outline</v-icon>
                <div>{{ $t('meusEnderecos.nenhum') }}</div>
            </div>

            <div
                v-for="endereco in enderecos"
                :key="endereco.uuid || endereco.id"
                class="end-item"
            >
                <v-icon class="end-item__icon" :color="primaryColor">
                    {{ tipoIcon(endereco.tipo_local) }}
                </v-icon>
                <div class="end-item__text">
                    <div class="end-item__title">
                        {{ enderecoTitulo(endereco) }}
                        <v-chip v-if="endereco.padrao" x-small label color="primary" class="ml-1">
                            {{ $t('meusEnderecos.padrao') }}
                        </v-chip>
                    </div>
                    <div class="end-item__sub">{{ enderecoResumo(endereco) }}</div>
                </div>
                <v-menu offset-y left>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small v-bind="attrs" v-on="on">
                            <v-icon small>mdi-dots-vertical</v-icon>
                        </v-btn>
                    </template>
                    <v-list dense>
                        <v-list-item v-if="!endereco.padrao" @click="definirPadrao(endereco)">
                            <v-icon small class="mr-2">mdi-star-outline</v-icon>
                            {{ $t('envio.definirPadrao') }}
                        </v-list-item>
                        <v-list-item @click="editar(endereco)">
                            <v-icon small class="mr-2">mdi-pencil-outline</v-icon>
                            {{ $t('meusEnderecos.editar') }}
                        </v-list-item>
                        <v-list-item @click="remover(endereco)">
                            <v-icon small class="mr-2" color="error">mdi-delete-outline</v-icon>
                            {{ $t('meusEnderecos.remover') }}
                        </v-list-item>
                    </v-list>
                </v-menu>
            </div>

            <v-btn
                :disabled="enderecos.length >= MAX_ENDERECOS"
                :color="primaryColor"
                outlined
                block
                class="mt-4"
                @click="novo"
            >
                <v-icon class="mr-1">mdi-plus</v-icon>
                {{ $t('meusEnderecos.adicionar') }}
                <span v-if="enderecos.length >= MAX_ENDERECOS" class="ml-1">{{ $t('meusEnderecos.maximo', { n: MAX_ENDERECOS }) }}</span>
            </v-btn>
        </template>

        <!-- Formulário -->
        <template v-else>
            <div class="d-flex align-center mb-3">
                <v-btn icon small @click="cancelar"><v-icon>mdi-arrow-left</v-icon></v-btn>
                <span class="font-weight-medium ml-1">
                    {{ form.uuid ? $t('envio.editarEndereco') : $t('meusEnderecos.novo') }}
                </span>
            </div>

            <v-text-field
                v-model="form.descricao"
                :label="$t('meusEnderecos.apelido')"
                :placeholder="$t('meusEnderecos.apelidoExemplo')"
                outlined dense hide-details class="mb-3"
            />

            <div v-if="exigeCep" class="d-flex" style="gap: 8px;">
                <v-text-field
                    v-model="form.cep"
                    :label="$t('endereco.codigoPostal.' + paisDaLoja.codigo)"
                    v-mask="[paisDaLoja.codigoPostal.mascara]"
                    :loading="loadingCep"
                    inputmode="numeric"
                    outlined dense hide-details class="mb-3"
                    @change="consultaCep"
                />
                <v-text-field
                    v-model="form.uf"
                    :label="$t('endereco.estado.' + paisDaLoja.codigo)"
                    maxlength="2"
                    outlined dense hide-details class="mb-3"
                    style="max-width: 90px;"
                />
            </div>

            <v-autocomplete
                v-if="showMunicipioAutocomplete"
                v-model="form.municipio"
                :items="municipios"
                item-text="descricao"
                item-value="descricao"
                :label="$t('meusEnderecos.municipio')"
                outlined dense hide-details class="mb-3"
                @change="form.bairro = null"
            />
            <v-text-field
                v-else
                v-model="form.municipio"
                :label="$t('meusEnderecos.municipio')"
                outlined dense hide-details class="mb-3"
            />

            <v-autocomplete
                v-if="showBairroAutocomplete"
                v-model="form.bairro"
                :items="getBairros"
                item-text="descricao"
                item-value="descricao"
                :label="$t('endereco.bairro')"
                outlined dense hide-details class="mb-3"
            />
            <v-text-field
                v-else
                v-model="form.bairro"
                :label="lojaNoBrasil ? $t('endereco.bairro') : $t('endereco.bairroOpcional')"
                outlined dense hide-details class="mb-3"
            />

            <div class="d-flex" style="gap: 8px;">
                <v-text-field
                    v-model="form.rua"
                    :label="$t('meusEnderecos.rua')"
                    outlined dense hide-details class="mb-3"
                />
                <v-text-field
                    v-model="form.numero"
                    :label="$t('endereco.numero')"
                    outlined dense hide-details class="mb-3"
                    style="max-width: 110px;"
                />
            </div>

            <v-text-field
                v-model="form.referencia"
                :label="$t('meusEnderecos.referencia')"
                outlined dense hide-details class="mb-3"
            />

            <v-radio-group v-model="form.tipo_local" row hide-details class="mt-0 mb-3">
                <v-radio value="casa">
                    <template #label><v-icon small class="mr-1">mdi-home-outline</v-icon>{{ $t('endereco.tipo.casa') }}</template>
                </v-radio>
                <v-radio value="apartamento">
                    <template #label><v-icon small class="mr-1">mdi-office-building-outline</v-icon>{{ $t('endereco.tipo.apartamento') }}</template>
                </v-radio>
                <v-radio value="trabalho">
                    <template #label><v-icon small class="mr-1">mdi-briefcase-outline</v-icon>{{ $t('endereco.tipo.trabalho') }}</template>
                </v-radio>
            </v-radio-group>

            <div v-if="form.tipo_local === 'apartamento'" class="d-flex" style="gap: 8px;">
                <v-text-field
                    v-model="form.nome_edificio"
                    :label="$t('endereco.nomePredio')"
                    outlined dense hide-details class="mb-3"
                />
                <v-text-field
                    v-model="form.numero_apartamento"
                    :label="$t('meusEnderecos.numeroApto')"
                    outlined dense hide-details class="mb-3"
                    style="max-width: 120px;"
                />
            </div>

            <v-checkbox
                v-model="form.padrao"
                :label="$t('meusEnderecos.usarComoPadrao')"
                hide-details class="mt-0 mb-3"
            />

            <v-btn :color="primaryColor" block dark :loading="saving" @click="salvar">
                <v-icon class="mr-1">mdi-content-save</v-icon>
                {{ $t('meusEnderecos.salvar') }}
            </v-btn>
        </template>
    </div>
</template>

<script>
import enderecosApi, { MAX_ENDERECOS } from '@/utils/enderecos';
import { codigoPostalValido, isBrasil, pais } from '@/utils/paises';
import { linhaEnderecoEua } from '@/utils/enderecoFormato';
import { mapState } from 'vuex';

const formVazio = () => ({
    uuid: null,
    descricao: null,
    cep: null,
    uf: null,
    municipio: null,
    bairro: null,
    rua: null,
    numero: null,
    referencia: null,
    tipo_local: null,
    nome_edificio: null,
    numero_apartamento: null,
    padrao: false,
});

export default {
    name: 'EnderecosManager',

    data: () => ({
        MAX_ENDERECOS,
        enderecos: [],
        loading: false,
        saving: false,
        loadingCep: false,
        showForm: false,
        form: formVazio(),
    }),

    computed: {
        ...mapState(['configuracoes']),

        primaryColor() {
            return (this.$theme && this.$theme.primary)
                || (this.$vuetify.theme.themes.light && this.$vuetify.theme.themes.light.primary)
                || '#d32f2f';
        },

        // Município/bairro atrelados à config de frete da empresa (mesma regra do checkout),
        // para que endereços criados aqui casem com a config e o frete resolva no envio.
        municipios() {
            return this.configuracoes?.municipios || [];
        },

        bairros() {
            return this.configuracoes?.bairros || [];
        },

        exigeCep() {
            return !!this.configuracoes?.obrigar_cep_cardapio;
        },

        cepValido() {
            return codigoPostalValido(this.form.cep, this.configuracoes?.pais);
        },

        paisDaLoja() {
            return pais(this.configuracoes?.pais);
        },

        lojaNoBrasil() {
            return isBrasil(this.configuracoes?.pais);
        },

        isFreteACombinar() {
            return this.configuracoes?.frete_tipo === 'A combinar';
        },

        isFretePorBairro() {
            return this.configuracoes?.frete_tipo === 'Bairro';
        },

        municipioSelId() {
            return this.municipios.find(m => m.descricao === this.form.municipio)?.id;
        },

        showMunicipioAutocomplete() {
            return this.municipios.length > 0;
        },

        showBairroAutocomplete() {
            if (this.isFreteACombinar) {
                return false;
            }
            const count = this.bairros.filter(b => b.municipio_id == this.municipioSelId).length;
            return count > 0 || this.isFretePorBairro;
        },

        getBairros() {
            if (!this.municipioSelId) {
                return this.bairros;
            }
            return this.bairros.filter(b => !b.municipio_id || b.municipio_id == this.municipioSelId);
        },
    },

    created() {
        this.carregar();
    },

    methods: {
        async carregar() {
            this.loading = true;
            try {
                this.enderecos = await enderecosApi.list();
            } catch (e) {
                this.notify(e.message || this.$t('meusEnderecos.erroCarregar'), 'warning');
            } finally {
                this.loading = false;
            }
        },

        novo() {
            this.form = formVazio();
            this.form.padrao = this.enderecos.length === 0;
            this.showForm = true;
        },

        editar(endereco) {
            this.form = { ...formVazio(), ...endereco };
            this.showForm = true;
        },

        cancelar() {
            this.showForm = false;
            this.form = formVazio();
        },

        async salvar() {
            if (!this.form.rua || !this.form.numero) {
                this.notify(this.$t('meusEnderecos.informeRuaNumero'), 'warning');
                return;
            }
            // o tipo é a chave da agenda (1 por tipo) — obrigatório
            if (!this.form.tipo_local) {
                this.notify(this.$t('meusEnderecos.selecioneTipo'), 'warning');
                return;
            }
            if (this.exigeCep && !this.cepValido) {
                this.notify(this.$t('envio.avisos.codigoPostalValido', { rotulo: this.$t('endereco.codigoPostal.' + this.paisDaLoja.codigo) }), 'warning');
                return;
            }

            this.saving = true;
            try {
                if (this.form.uuid) {
                    await enderecosApi.update(this.form.uuid, this.form);
                } else {
                    // sem uuid do cliente: autenticado → servidor gera; convidado → uuid local em create()
                    await enderecosApi.create(this.form);
                }
                await this.carregar();
                this.showForm = false;
                this.form = formVazio();
                this.notify(this.$t('meusEnderecos.salvo'));
            } catch (e) {
                this.notify(e.message || this.$t('meusEnderecos.erroSalvar'), 'warning');
            } finally {
                this.saving = false;
            }
        },

        async definirPadrao(endereco) {
            try {
                await enderecosApi.setPadrao(endereco.uuid);
                await this.carregar();
            } catch (e) {
                this.notify(e.message || this.$t('meusEnderecos.erroPadrao'), 'warning');
            }
        },

        async remover(endereco) {
            if (!await this.$root.$confirm(this.$t('meusEnderecos.removerTitulo'), this.$t('meusEnderecos.removerTexto'), { color: 'red darken-1' })) {
                return;
            }
            try {
                await enderecosApi.remove(endereco.uuid);
                await this.carregar();
                this.notify(this.$t('meusEnderecos.removido'));
            } catch (e) {
                this.notify(e.message || this.$t('meusEnderecos.erroRemover'), 'warning');
            }
        },

        // ViaCEP só existe para o Brasil; o ZIP americano não tem consulta.
        async consultaCep() {
            if (!this.lojaNoBrasil || !this.cepValido) {
                return;
            }

            const cep = (this.form.cep || '').replace(/\D/g, '');
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), 4000);

            this.loadingCep = true;
            try {
                const resp = await fetch(`https://viacep.com.br/ws/${cep}/json`, { signal: controller.signal })
                    .then(r => r.json());

                if (resp.erro) {
                    return;
                }

                this.form.uf = this.form.uf || resp.uf || null;
                this.form.rua = this.form.rua || resp.logradouro || null;
                // município/bairro: no modo autocomplete, casa com a config (senão texto livre)
                this.form.municipio = this.form.municipio
                    || this.matchConfig(this.municipios, resp.localidade, this.showMunicipioAutocomplete);
                this.form.bairro = this.form.bairro
                    || this.matchConfig(this.getBairros, resp.bairro, this.showBairroAutocomplete);
            } catch (e) {
                // offline, timeout ou CEP inexistente: segue com o que o cliente digitou
            } finally {
                clearTimeout(timer);
                this.loadingCep = false;
            }
        },

        // casa o valor do CEP com a config (case-insensitive). No modo autocomplete devolve a
        // descrição canônica da config (ou null p/ o usuário selecionar); senão o texto livre.
        matchConfig(items, valor, autocomplete) {
            if (!autocomplete) {
                return valor || null;
            }
            const norm = v => (v || '').toString().trim().toLowerCase();
            const found = (items || []).find(i => norm(i.descricao) === norm(valor));
            return found ? found.descricao : null;
        },

        tipoIcon(tipo) {
            return {
                casa: 'mdi-home-outline',
                apartamento: 'mdi-office-building-outline',
                trabalho: 'mdi-briefcase-outline',
            }[tipo] || 'mdi-map-marker-outline';
        },

        enderecoTitulo(e) {
            if (e.descricao) {
                return e.descricao;
            }
            return this.$t('endereco.tipo.' + (['casa', 'apartamento', 'trabalho'].includes(e.tipo_local) ? e.tipo_local : 'endereco'));
        },

        enderecoResumo(e) {
            if (!this.lojaNoBrasil) {
                const apto = e.tipo_local === 'apartamento'
                    ? [e.nome_edificio, e.numero_apartamento].filter(Boolean).join(', ')
                    : '';
                return [linhaEnderecoEua(e), e.bairro, apto, e.referencia].filter(Boolean).join(' · ');
            }
            const linhaRua = [e.rua, e.numero].filter(Boolean).join(', ');
            const bairroMun = [e.bairro, e.municipio].filter(Boolean).join(' - ');
            const partes = [linhaRua, bairroMun];
            if (e.tipo_local === 'apartamento') {
                const apto = [e.nome_edificio, e.numero_apartamento].filter(Boolean).join(', ');
                apto && partes.push(apto);
            }
            e.referencia && partes.push(e.referencia);
            return partes.filter(Boolean).join(' · ');
        },
    },
}
</script>

<style scoped>
.end-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 2px;
}

.end-item + .end-item {
    border-top: 1px solid #f2f2f2;
}

.end-item__icon {
    flex-shrink: 0;
}

.end-item__text {
    min-width: 0;
    flex: 1 1 auto;
}

.end-item__title {
    font-weight: 600;
    font-size: 0.95rem;
}

.end-item__sub {
    font-size: 0.8rem;
    color: #9e9e9e;
    word-break: break-word;
}
</style>
